import * as React from 'react';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import DiseaseTable from './DiseaseTable';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import KnowledgeService from '@lifeomic/phc-web-toolkit/src/services/KnowledgeService';
import TableTitle from '@lifeomic/phc-web-toolkit/src/components/Table/TableTitle';
import urls from '../../../../src/util/urls';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { BarChart } from './Visualizations/BarChart';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { defineMessages, useIntl } from 'react-intl';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { DNAIcon } from '../../../../src/components/common/icons';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Info } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { useStyles as useLinkStyles } from '@lifeomic/chroma-react/components/Link/Link';

const knowledgeService = new KnowledgeService();

const useStyles = makeStyles(theme => ({
  tableContainer: {
    width: '100%',
    overflow: 'auto',
    maxHeight: `calc(50vh - ${theme.pxToRem(200)})`,
  },
  infoIcon: {
    marginLeft: theme.spacing(1),
  },
}));

const messages = defineMessages({
  title: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.geneDetails',
    defaultMessage: 'Gene Details',
  },
  noGenes: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.noGenes',
    defaultMessage: 'No gene details found',
  },
  noCancerDrivers: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.noCancerDrivers',
    defaultMessage:
      '{geneLink} has not been detected as a mutational cancer driver',
  },
  noCancerDriversMultiGene: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.noCancerDriversMultiGene',
    defaultMessage:
      '{geneLink1} and {geneLink2} have not been detected as mutational cancer drivers',
  },
  cancerDriversTableTitle: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.cancerDriversTableTitle',
    defaultMessage: 'Gene Mutation Frequency in Various Cancer Types',
  },
  cancerDriversTableInfo: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.cancerDriversTableInfo',
    defaultMessage:
      'Bar chart and table show details of published cancer studies where the gene is predicted to act as a cancer driver. All studies are listed in the table and those cohorts with at least 30 samples are plotted in the bar chart. https://intogen.org/',
  },
  cancerDriversChartYTitle: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.cancerDriversChartYTitle',
    defaultMessage: 'Positive Samples in Cohort',
  },
  cancerDriversChartXTitle: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.cancerDriversChartXTitle',
    defaultMessage: 'Cancer Type',
  },
  cancerDriversChartHoverText: {
    id: 'omicsExplorer.variantDetails.cancerPrevalenceContent.cancerDriversChartHoverText',
    defaultMessage:
      '{cohortDetails}</br>{cohortAge}, {cohortType}, {cohortTreated}, {cohortPlatform}</br>Samples {samples} / {cohortSamples}',
  },
});

const getVariantGene = (variant: any) =>
  (variant.ensemblCanon?.length > 0 && variant.ensemblCanon[0].gene) ||
  (variant.ensembl?.length > 0 && variant.ensembl[0].gene);

const getBarChartData = (gene: any, formatMessage: any) => {
  if (gene.topCancerDrivers) {
    return [
      {
        x: gene.topCancerDrivers.map((g: any) => g.cohortName),
        y: gene.topCancerDrivers.map((g: any) => g.percentSamplesCohort),
        hovertext: gene.topCancerDrivers.map((g: any) =>
          formatMessage(messages.cancerDriversChartHoverText, {
            cohortDetails: g.cohortDetails,
            cohortAge: g.cohortAge,
            cohortType: g.cohortType,
            cohortTreated: g.cohortTreated,
            cohortPlatform: g.cohortPlatform,
            samples: g.samples,
            cohortSamples: g.cohortSamples,
          }),
        ),
        type: 'bar',
      },
    ];
  }
  return [];
};

interface CancerPrevalenceContentProps {
  contentClassName?: string;
  data: any;
  emptyStateClassName?: string;
}

export const CancerPrevalenceContent = ({
  contentClassName,
  data,
  emptyStateClassName,
}: CancerPrevalenceContentProps) => {
  const [getGenes, getGenesState] = useService(knowledgeService.getGenes);
  const [genes, setGenes] = React.useState([]);

  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const linkClasses = useLinkStyles({});

  const hasCancerDrivers = genes.some(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    gene => gene.topCancerDrivers || gene.cancerDrivers,
  );

  const geneLink = ({
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    gene,
  }) => {
    return (
      <a
        className={linkClasses.root}
        data-testid={`gene-link-${gene}`}
        href={urls.intogen.search(gene)}
        rel="noopener noreferrer"
        target="_blank"
      >
        {gene}
      </a>
    );
  };

  React.useEffect(() => {
    const geneIds = [
      getVariantGene(data),
      data.gene,
      data.geneId,
      data.geneId1,
      data.geneId2,
    ].filter(g => !!g);

    const fetchGenes = async () => {
      if (geneIds.length > 0) {
        const response = await getGenes({
          gene: [...new Set(geneIds)].join(','),
        });

        if (response?.data?.items) {
          setGenes(response.data.items.filter((g: any) => g.name));
        }
      }
    };
    fetchGenes();
  }, [data, getGenes]);

  if (!!getGenesState.lastFetchAttempt && genes.length === 0) {
    return (
      <div className={emptyStateClassName}>
        <EmptyPageState
          message={formatMessage(messages.noGenes)}
          illustration={DNAIcon}
        />
      </div>
    );
  }

  if (getGenesState.isFetching) {
    return (
      <Box align="center" data-testid="loader" fullHeight justify="center">
        <DotLoader />
      </Box>
    );
  }

  if (hasCancerDrivers) {
    return (
      <Paper className={contentClassName} padding={0}>
        {genes.map((gene, index) => (
          <div key={index}>
            {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
            {(gene.topCancerDrivers || gene.cancerDrivers) && (
              <TableTitle
                title={formatMessage(messages.cancerDriversTableTitle)}
                titleActions={
                  <Tooltip
                    title={formatMessage(messages.cancerDriversTableInfo)}
                  >
                    <span className={classes.infoIcon}>
                      <Info
                        aria-label={formatMessage(
                          messages.cancerDriversTableInfo,
                        )}
                        width={18}
                        height={18}
                      />
                    </span>
                  </Tooltip>
                }
              >
                {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
                {gene.topCancerDrivers && (
                  <BarChart
                    data={getBarChartData(gene, formatMessage)}
                    xtitle={formatMessage(messages.cancerDriversChartXTitle)}
                    ytitle={formatMessage(messages.cancerDriversChartYTitle)}
                  />
                )}
                {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
                {gene.cancerDrivers && (
                  <DiseaseTable
                    contentClassName={classes.tableContainer}
                    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                    data={gene.cancerDrivers}
                  />
                )}
                {index < genes.length - 1 && <Divider />}
              </TableTitle>
            )}
          </div>
        ))}
      </Paper>
    );
  } else if (genes.length && !hasCancerDrivers) {
    return (
      <div className={emptyStateClassName}>
        <EmptyPageState
          message={
            genes.length > 1
              ? formatMessage(messages.noCancerDriversMultiGene, {
                  geneLink1: geneLink(genes?.[0]),
                  geneLink2: geneLink(genes?.[1]),
                })
              : formatMessage(messages.noCancerDrivers, {
                  geneLink: geneLink(genes?.[0]),
                })
          }
          illustration={DNAIcon}
        />
      </div>
    );
  } else {
    return null;
  }
};
